// Phase II / Phase D (2026-05-17) — client-side presence heartbeat.
//
// While the dashboard is mounted and the document is visible, fires a
// `heartbeat` mutation every HEARTBEAT_INTERVAL_MS. Pauses while the tab
// is hidden (no point posting heartbeats nobody can act on) and resumes
// on visibility change with an immediate beat so the user reads online
// again quickly.
//
// Caller passes in the bound mutation (so the hook stays test-friendly and
// doesn't require the Convex runtime at parse time).

import React from 'react';

export const HEARTBEAT_INTERVAL_MS = 15 * 1000;

export const usePresenceHeartbeat = ({ heartbeat, enabled = true }) => {
  React.useEffect(() => {
    if (!enabled || typeof heartbeat !== 'function') return undefined;
    let cancelled = false;
    let intervalId = null;

    const beat = async () => {
      if (cancelled) return;
      try {
        await heartbeat();
      } catch (err) {
        // Heartbeats fail open. A missed beat means we read as offline
        // for ~15s and recover on the next interval. Logging at warn so
        // a broken auth state still surfaces in console.
        if (typeof console !== 'undefined') console.warn('presence heartbeat failed', err);
      }
    };

    const startInterval = () => {
      if (intervalId != null) return;
      intervalId = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    };
    const stopInterval = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        beat();           // immediate catch-up beat
        startInterval();
      } else {
        stopInterval();
      }
    };

    // Initial state: beat once + start interval if visible.
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      beat();
      startInterval();
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      cancelled = true;
      stopInterval();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [heartbeat, enabled]);
};
